import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { LegalPageLayout } from "@/components/LegalPageLayout";
import { LEGAL_LAST_UPDATED, LEGAL_LAST_UPDATED_AR } from "@/lib/legal";

export const Route = createFileRoute("/device-policy")({
  head: () => ({
    meta: [
      { title: "Device & Account Security Policy — Lumvio" },
      {
        name: "description",
        content:
          "Each Lumvio account is limited to 3 registered devices. Learn how the device limit protects your account and how to reset your devices.",
      },
      { property: "og:title", content: "Device & Account Security — Lumvio" },
      {
        property: "og:description",
        content:
          "Lumvio limits each account to 3 registered devices to prevent account sharing and unauthorized access.",
      },
      { property: "og:url", content: "https://lumvio.co/device-policy" },
    ],
    links: [{ rel: "canonical", href: "https://lumvio.co/device-policy" }],
  }),
  component: DevicePolicyPage,
});

function DevicePolicyPage() {
  const { i18n } = useTranslation();
  const ar = (i18n.language || "en").startsWith("ar");

  return (
    <LegalPageLayout
      title={ar ? "أمن الجهاز والحساب" : "Device & Account Security"}
      updated={ar ? LEGAL_LAST_UPDATED_AR : LEGAL_LAST_UPDATED}
    >
      {ar ? <ArabicBody /> : <EnglishBody />}
    </LegalPageLayout>
  );
}

function EnglishBody() {
  return (
    <>
      <p>
        To protect your account and prevent unauthorized sharing, each Lumvio account is limited to a maximum of{" "}
        <strong>3 registered devices</strong>.
      </p>

      <h2>How the device limit works</h2>
      <ul>
        <li>When you sign in from a new device, it is registered to your account automatically.</li>
        <li>Once you reach 3 devices, additional sign-ins are blocked until a device is removed.</li>
        <li>Different browsers on the same computer may count as separate devices.</li>
        <li>
          To change or reset your registered devices, contact support at{" "}
          <a href="mailto:contact@lumvio.co">contact@lumvio.co</a>.
        </li>
        <li>Repeated attempts to bypass this limit or share accounts may result in account suspension.</li>
      </ul>

      <h2>What we track</h2>
      <p>
        For security purposes only, we track the following information for each registered device: device type,
        browser, approximate location (derived from IP), and last activity timestamp. We do not use this information
        for advertising or share it with third parties outside of what is described in our Privacy Policy.
      </p>

      <h2>Reset requests</h2>
      <p>
        If you have lost access to a device or need to unregister it, email{" "}
        <a href="mailto:contact@lumvio.co">contact@lumvio.co</a> from your account's registered email address. Include
        the device name or approximate date it was registered so we can verify your request.
      </p>

      <h2>Why we enforce this</h2>
      <p>
        The 3-device limit is a fraud-prevention control. It protects payment protection balances, verified identity, and client
        conversations from being accessed by unauthorized parties, and it discourages account sharing that would
        undermine the trust the platform is built on.
      </p>
    </>
  );
}

function ArabicBody() {
  return (
    <>
      <p>
        لحماية حسابك ومنع المشاركة غير المصرح بها، يقتصر كل حساب في لومڤيو على{" "}
        <strong>3 أجهزة مسجّلة كحد أقصى</strong>.
      </p>

      <h2>كيف يعمل حد الأجهزة</h2>
      <ul>
        <li>عند تسجيل الدخول من جهاز جديد، يتم تسجيله تلقائيًا في حسابك.</li>
        <li>عند الوصول إلى 3 أجهزة، يتم حظر أي تسجيل دخول إضافي حتى تتم إزالة أحد الأجهزة.</li>
        <li>قد تُعدّ المتصفحات المختلفة على الجهاز نفسه أجهزةً منفصلة.</li>
        <li>
          لتغيير أو إعادة ضبط الأجهزة المسجّلة، تواصل مع الدعم عبر{" "}
          <a href="mailto:contact@lumvio.co">contact@lumvio.co</a>.
        </li>
        <li>قد تؤدي المحاولات المتكررة لتجاوز هذا الحد أو مشاركة الحساب إلى تعليق الحساب.</li>
      </ul>

      <h2>ما الذي نتتبعه</h2>
      <p>
        لأغراض أمنية فقط، نتتبع لكل جهاز مسجّل: نوع الجهاز، المتصفح، الموقع التقريبي (من عنوان IP)، ووقت آخر نشاط. لا
        نستخدم هذه المعلومات للإعلانات ولا نشاركها مع أطراف ثالثة خارج ما هو موضح في سياسة الخصوصية.
      </p>

      <h2>طلبات إعادة الضبط</h2>
      <p>
        إذا فقدت الوصول إلى جهاز أو احتجت إلى إلغاء تسجيله، أرسل بريدًا إلى{" "}
        <a href="mailto:contact@lumvio.co">contact@lumvio.co</a> من عنوان البريد المسجّل في حسابك مع ذكر اسم الجهاز أو
        التاريخ التقريبي لتسجيله للتحقق من طلبك.
      </p>

      <h2>لماذا نطبّق هذا الحد</h2>
      <p>
        حد الأجهزة الثلاثة هو ضابط لمكافحة الاحتيال. يحمي أرصدة الضمان والهوية الموثّقة ومحادثات العملاء من الوصول غير
        المصرح به، ويحدّ من مشاركة الحسابات التي تُضعف الثقة التي بُنيت عليها المنصة.
      </p>
    </>
  );
}
